import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, User as UserIcon } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LoginPage({ onLogin }) {
  const [mode, setMode] = useState('checking'); // 'checking' | 'login' | 'bootstrap'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/bootstrap-status`);
        setMode(res.data.admin_exists ? 'login' : 'bootstrap');
      } catch (err) {
        setMode('login');
      }
    })();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      toast.success('Login successful');
      onLogin(response.data.user, response.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleBootstrap = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { email, password, name, role: 'admin' });
      const loginRes = await axios.post(`${API}/auth/login`, { email, password });
      toast.success('Admin account created — welcome!');
      onLogin(loginRes.data.user, loginRes.data.token);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const isBootstrap = mode === 'bootstrap';

  return (
    <div className="flex h-screen" data-testid="login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjB3YXJlaG91c2UlMjBpbnRlcmlvciUyMGxvZ2lzdGljc3xlbnwwfHx8fDE3ODU5MDMzMzl8MA&ixlib=rb-4.1.0&q=85"
          alt="Warehouse"
          className="object-cover w-full h-full"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-8 left-8 text-white">
          <h2 className="heading text-4xl font-black mb-2">Warehouse Management</h2>
          <p className="text-lg text-zinc-200">Optimize your warehouse operations</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-[#F4F4F5]">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            {isBootstrap ? (
              <>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#DC2626]/10 mb-4">
                  <ShieldCheck size={28} weight="fill" className="text-[#DC2626]" />
                </div>
                <h1 className="heading text-4xl font-black text-[#09090B] mb-2" data-testid="bootstrap-title">
                  Create First Admin
                </h1>
                <p className="text-[#52525B]">No accounts exist yet. Set up the master admin so you can create the rest of your team.</p>
              </>
            ) : (
              <>
                <h1 className="heading text-4xl font-black text-[#09090B] mb-2" data-testid="login-title">
                  Sign In
                </h1>
                <p className="text-[#52525B]">Enter the credentials your admin gave you</p>
              </>
            )}
          </div>

          {mode === 'checking' ? (
            <div className="card text-center py-8 text-[#52525B]">Loading...</div>
          ) : isBootstrap ? (
            <div className="card">
              <form onSubmit={handleBootstrap} className="space-y-5">
                <div>
                  <label htmlFor="b_name" className="block text-sm font-medium text-[#09090B] mb-2">Full Name</label>
                  <input id="b_name" type="text" value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full px-4 py-2.5 border border-[#E4E4E7] rounded-md focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
                    placeholder="Warehouse Owner" required data-testid="bootstrap-name-input" />
                </div>
                <div>
                  <label htmlFor="b_email" className="block text-sm font-medium text-[#09090B] mb-2">Email Address</label>
                  <input id="b_email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2.5 border border-[#E4E4E7] rounded-md focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
                    placeholder="you@company.com" required data-testid="bootstrap-email-input" />
                </div>
                <div>
                  <label htmlFor="b_password" className="block text-sm font-medium text-[#09090B] mb-2">Password (min 6 chars)</label>
                  <input id="b_password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 border border-[#E4E4E7] rounded-md focus:outline-none focus:ring-2 focus:ring-[#DC2626] focus:border-transparent"
                    placeholder="••••••••" required minLength={6} data-testid="bootstrap-password-input" />
                </div>
                <button type="submit" disabled={loading} className="w-full btn-primary h-11" data-testid="bootstrap-submit-button">
                  {loading ? 'Creating admin...' : 'Create Admin & Sign In'}
                </button>
              </form>
            </div>
          ) : (
            <div className="card">
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#09090B] mb-2">Email Address</label>
                  <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2.5 border border-[#E4E4E7] rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF3B30] focus:border-transparent"
                    placeholder="you@company.com" required data-testid="login-email-input" />
                </div>
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-[#09090B] mb-2">Password</label>
                  <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 border border-[#E4E4E7] rounded-md focus:outline-none focus:ring-2 focus:ring-[#FF3B30] focus:border-transparent"
                    placeholder="••••••••" required data-testid="login-password-input" />
                </div>
                <button type="submit" disabled={loading} className="w-full btn-primary h-11" data-testid="login-submit-button">
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
              <div className="mt-6 p-4 bg-[#F4F4F5] rounded-md border border-[#E4E4E7] flex items-start gap-3">
                <UserIcon size={16} className="text-[#52525B] mt-0.5" weight="duotone" />
                <p className="text-xs text-[#52525B]">Ask your warehouse admin for your account. They can create users under Users in the sidebar.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
